import React, { useState, useEffect } from "react";
import {
  GraphicPanel,
  Row,
  ItemRegister,
  ItemLabel,
  RowButton,
  Button,
  FormularioRegister,
  Item,
} from "../../components/Login/styles";
import Loading from "../../components/general/loading";
import PasswordValidation from "../../components/Login/PasswordValidation";
import ErrorLabel from "../../components/general/ErrorLabel";
import Logo from "../../components/general/Logo";
import Modal from "../../components/general/Modal";
import CustomNotification from "../../components/general/CustomNotification";
import AvisoPrivacidad from "../../components/Login/AvisoPrivacidad";
import Terminos from "../../components/Login/Terminos";
import useNotifications from "../../hooks/useNotification";
import { useModal } from "../../hooks/useModal";
import {
  signUpGuest,
  confirmSignUp,
  registerGuestInBackend,
} from "../../components/Login/utils/AuthService";

const GuestFormPanel = () => {
  const [step, setStep] = useState("registration");
  const [loading, setLoading] = useState(false);
  const [emptyName, setEmptyName] = useState(true);
  const [emptyLastName, setEmptyLastName] = useState(true);
  const [emptyEmail, setEmptyEmail] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [hasUpperCase, setHasUpperCase] = useState(false);
  const [hasLowerCase, setHasLowerCase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [userSub, setUserSub] = useState("");
  const [code, setCode] = useState("");
  const [emptyCode, setEmptyCode] = useState(true);
  const [awsError, setAwsError] = useState("");
  const { notifications, addNotification, deleteNotification } =
    useNotifications();
  const { modalData, openModal, closeModal, handleCancelClick } = useModal();

  useEffect(() => {
    validateForm();
  }, [
    name,
    lastName,
    email,
    password,
    confirmPassword,
    isLengthValid,
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSpecialChar,
  ]);

  const validateForm = () => {
    const isNameValid = name.trim() !== "";
    const isLastNameValid = lastName.trim() !== "";
    const isEmailValid = email.trim() !== "";
    const isPasswordValid =
      isLengthValid &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar;
    const isConfirmPasswordValid = confirmPassword === password;

    setIsFormValid(
      isNameValid &&
        isLastNameValid &&
        isEmailValid &&
        isPasswordValid &&
        isConfirmPasswordValid,
    );
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
    validateForm();
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value !== password) {
      setErrorMessage("Las contraseñas no coinciden");
    } else {
      setErrorMessage("");
    }
    validateForm();
  };

  const handleOpenTerms = () => {
    openModal({
      title: "Términos y Condiciones",
      customComponent: <Terminos />,
      showAccept: true,
      showCancel: false,
      onAcceptClick: closeModal,
    });
  };

  const handleOpenNotice = () => {
    openModal({
      title: "Aviso de Privacidad",
      customComponent: <AvisoPrivacidad />,
      showAccept: true,
      showCancel: false,
      onAcceptClick: closeModal,
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    validateForm();
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }

    const userData = {
      name: name,
      lastName: lastName,
      email: email,
      password: password,
    };

    try {
      setLoading(true);
      const result = await signUpGuest(userData);

      if (result.success) {
        setUserSub(result.userSub);
        addNotification(
          "success",
          `Se ha enviado el código de verificación al correo ${email}`,
        );
        setStep("verification");
      } else {
        if (result.error === "emailExists") {
          addNotification("error", "El correo electrónico ya está registrado");
        } else {
          addNotification("error", "Error al registrarse");
        }
      }
    } catch (error) {
      console.error("Error de registro:", error);
      addNotification("error", "Error al registrarse.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setShowErrors(true);

    if (code.length === 0) {
      setEmptyCode(true);
      return;
    }

    try {
      setLoading(true);
      const result = await confirmSignUp(email, code);

      if (result.success) {
        const userData = {
          name: name,
          lastName: lastName,
          email: email,
          userSub: userSub,
        };

        const backendResult = await registerGuestInBackend(userData);

        if (backendResult.success) {
          addNotification("success", "Registro completado exitosamente");
          setStep("success");
        } else {
          addNotification("error", "Error al completar el registro");
        }
      } else {
        setAwsError(result.error);
        addNotification("error", `Error: ${result.error}`);
      }
    } catch (err) {
      console.error("Error confirmando el código:", err);
      addNotification("error", "Error al verificar el código de confirmación");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePassword = (password) => {
    setIsLengthValid(password.length >= 8);
    setHasUpperCase(/[A-Z]/.test(password));
    setHasLowerCase(/[a-z]/.test(password));
    setHasNumber(/\d/.test(password));
    setHasSpecialChar(/[!@#$%^&*(),.?":{}|<>]/.test(password));
    validateForm();
  };

  const renderRegistrationForm = () => (
    <FormularioRegister>
      <Logo />
      <div className="container-register">
        <p className="title">Registro de Invitado</p>
        <Row>
          <label>Nombre</label>
          <ItemRegister>
            <input
              required
              id="nameInput"
              placeholder="Escribe tu nombre"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setEmptyName(e.target.value.trim() === "");
                validateForm();
              }}
            />
          </ItemRegister>
        </Row>
        <ErrorLabel
          showErrors={showErrors}
          condition={emptyName}
          message="Ingrese su Nombre"
        />
        <Row>
          <label>Apellidos</label>
          <ItemRegister>
            <input
              required
              id="lastNameInput"
              placeholder="Escribe tus apellidos"
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setEmptyLastName(e.target.value.trim() === "");
                validateForm();
              }}
            />
          </ItemRegister>
        </Row>
        <ErrorLabel
          showErrors={showErrors}
          condition={emptyLastName}
          message="Ingrese sus Apellidos"
        />
        <Row>
          <label>Correo Electrónico</label>
          <ItemRegister>
            <input
              required
              id="emailInput"
              placeholder="Escribe tu correo electrónico"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmptyEmail(e.target.value.trim() === "");
                validateForm();
              }}
            />
          </ItemRegister>
        </Row>
        <ErrorLabel
          showErrors={showErrors}
          condition={emptyEmail}
          message="Ingrese su correo electrónico"
        />
        <Row>
          <label>Crear contraseña</label>
          <ItemRegister>
            <input
              required
              id="passwordInput"
              placeholder="Escribe tu contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
            />
            <span
              onClick={togglePasswordVisibility}
              className="material-icons"
              style={{ cursor: "pointer", color: "#CBCBCB", fontSize: "18px" }}
            >
              {showPassword ? "visibility_off" : "visibility"}
            </span>
          </ItemRegister>
        </Row>
        <PasswordValidation
          isLengthValid={isLengthValid}
          hasUpperCase={hasUpperCase}
          hasLowerCase={hasLowerCase}
          hasNumber={hasNumber}
          hasSpecialChar={hasSpecialChar}
        />
        <Row>
          <label>Confirmar contraseña</label>
          <ItemRegister>
            <input
              required
              id="confirmPasswordInput"
              placeholder="Confirma tu contraseña"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
            />
            <span
              onClick={toggleConfirmPasswordVisibility}
              className="material-icons"
              style={{ cursor: "pointer", color: "#CBCBCB", fontSize: "18px" }}
            >
              {showConfirmPassword ? "visibility_off" : "visibility"}
            </span>
          </ItemRegister>
        </Row>
        {errorMessage && <p className="error">{errorMessage}</p>}
        <RowButton>
          <Button type="submit" onClick={onSubmit}>
            Registrarse
          </Button>
        </RowButton>
      </div>
      <Row>
        <ItemLabel className="row">
          <label>
            Al completar el registro aceptas los{" "}
            <span onClick={handleOpenTerms}>Términos y condiciones</span> y el{" "}
            <span onClick={handleOpenNotice}>Aviso de privacidad</span>.
          </label>
        </ItemLabel>
      </Row>
    </FormularioRegister>
  );

  const renderVerificationForm = () => (
    <FormularioRegister>
      <Logo />
      <div className="container-register">
        <p className="title">Código de verificación</p>
        <Row>
          <label>Ingrese el código de verificación enviado a su correo</label>
          <ItemRegister>
            <input
              required
              id="codeInput"
              placeholder="XXXXXX"
              type="number"
              value={code}
              min="0"
              onChange={(e) => {
                setCode(e.target.value);
                setEmptyCode(e.target.value.trim() === "");
              }}
            />
          </ItemRegister>
        </Row>
        <ErrorLabel
          showErrors={showErrors}
          condition={emptyCode}
          message="Ingrese su código de verificación"
        />
        {awsError === "LimitExceededException" && !emptyCode && (
          <ErrorLabel
            showErrors={showErrors}
            condition={true}
            message="Haz realizado demasiados intentos, intentalo más tarde"
          />
        )}
        {awsError === "CodeMismatchException" && !emptyCode && (
          <ErrorLabel
            showErrors={showErrors}
            condition={true}
            message="Código de verificación incorrecto"
          />
        )}
        {awsError === "ExpiredCodeException" && !emptyCode && (
          <ErrorLabel
            showErrors={showErrors}
            condition={true}
            message="El código ingresado está expirado"
          />
        )}
        <RowButton>
          <Button type="button" onClick={handleVerifyCode}>
            Verificar
          </Button>
        </RowButton>
      </div>
      <Row>
        <Item>
          <Button
            type="button"
            onClick={() =>
              addNotification(
                "info",
                "Se ha reenviado el código de verificación",
              )
            }
          >
            Reenviar código de verificación
          </Button>
        </Item>
      </Row>
    </FormularioRegister>
  );

  const renderSuccessMessage = () => (
    <FormularioRegister>
      <Logo />
      <div className="container-register">
        <p className="title">¡Registro exitoso!</p>
        <Row>
          <label>
            Tu cuenta ha sido creada correctamente. Ya puedes iniciar sesión.
          </label>
        </Row>
        <RowButton>
          <Button
            type="button"
            onClick={() => (window.location.href = "/login")}
          >
            Ir a iniciar sesión
          </Button>
        </RowButton>
      </div>
    </FormularioRegister>
  );

  const renderStep = () => {
    switch (step) {
      case "registration":
        return renderRegistrationForm();
      case "verification":
        return renderVerificationForm();
      case "success":
        return renderSuccessMessage();
      default:
        return renderRegistrationForm();
    }
  };

  return (
    <GraphicPanel>
      {loading ? <Loading /> : renderStep()}
      <CustomNotification
        open={notifications.length > 0}
        alerts={notifications}
        deleteNotification={deleteNotification}
      />
      <Modal {...modalData} onClose={handleCancelClick} />
    </GraphicPanel>
  );
};

export default GuestFormPanel;
